"use client";

import React, { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
const milestones = [{
  year: "2025",
  title: "UK Registration",
  description: "The Farquhar Fisher Foundation was officially registered as a UK charity, creating a strong foundation for transparent and meaningful work in support of education and community development in Ghana.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602004/img/timeline_spark.png"
}, {
  year: "2025",
  title: "ICT Centre Project",
  description: "We completed an Information and Communication Technology (ICT) Centre at Ajumako Baa in the Central Region of Ghana to improve digital learning and access to technology.",
  image: "https://farquharfisherfoundation.co.uk/wp-content/uploads/2026/03/17.jpeg?v=f3yu41a05kg"
}, {
  year: "2025",
  title: "School Transport Support",
  description: "We provided a small van to help transport children safely to and from school, improving access to education and making school attendance easier for families.",
  image: "https://farquharfisherfoundation.co.uk/wp-content/uploads/2026/03/3.jpeg?v=e1xx31656la"
}, {
  year: "Next Step",
  title: "Growing Our Impact",
  description: "As a new organisation, we are continuing to plan additional projects that will expand our reach and create more opportunities for children and communities in Ghana.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602004/img/timeline_creatiforge.png"
}];
function TimelineItem({
  item,
  index
}: {
  item: (typeof milestones)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="141860" ref={ref} data-index={index} className={`relative mb-12 flex w-full items-center transition-all duration-1000 ease-out last:mb-0 sm:mb-14 md:mb-16 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
      <div className="absolute left-8 z-10 h-5 w-5 -translate-x-1/2 rounded-full border-2 border-primary bg-secondary shadow-[0_0_15px_var(--color-secondary)] transition-all duration-500 md:left-1/2 animate-pulse" />

      <div className={`w-full pl-20 pr-4 md:w-[calc(50%-4rem)] md:px-0 ${isEven ? "md:mr-auto" : "md:ml-auto"}`}>
        <Card className="group overflow-hidden border-border/50 bg-card shadow-sm transition-all hover:border-secondary/40 hover:shadow-md backdrop-blur-md">
          <div className="flex flex-col gap-0 p-1 sm:flex-row sm:gap-6">
            <div className="w-full shrink-0 p-3 pb-0 sm:w-36 sm:p-4 sm:pb-4">
              <AspectRatio ratio={1} className="overflow-hidden rounded-xl">
                <img src={item.image} alt={item.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="pointer-events-none absolute inset-0 bg-secondary/10 mix-blend-overlay" />
              </AspectRatio>
            </div>

            <div className="flex flex-col justify-center p-4 sm:p-4 sm:pl-0">
              <Badge className="mb-3 w-fit border-secondary/20 bg-secondary/10 text-primary transition-colors hover:bg-secondary/20">
                {item.year}
              </Badge>

              <h3 className="mb-2 text-lg font-bold tracking-tight text-foreground sm:text-xl">
                {item.title}
              </h3>

              <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                {item.description}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>;
}
export default function TheArtistsPath() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const start = windowHeight / 2;
      const end = start - rect.height;
      let progress = 0;
      if (rect.top > start) {
        progress = 0;
      } else if (rect.top < end) {
        progress = 100;
      } else {
        progress = (start - rect.top) / rect.height * 100;
      }
      setScrollProgress(progress);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="our-journey" className="relative overflow-hidden bg-background text-foreground ![padding:40px_0px_40px_0px]">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="mx-auto mb-12 max-w-3xl text-center sm:mb-14 md:mb-16">
          <h2 className="mb-4 text-3xl font-bold tracking-tight text-primary sm:text-4xl md:text-5xl">
            Our Journey So Far
          </h2>
          <p className="text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
            As a new organisation, our story is just beginning. These key
            milestones reflect our commitment to supporting education and
            creating opportunity for children and communities in Ghana.
          </p>
        </div>

        <div ref={containerRef} className="relative mx-auto max-w-6xl">
          <div className="absolute bottom-0 left-8 top-0 w-[2px] bg-border/50 md:left-1/2 md:-translate-x-1/2" />
          <div className="absolute left-8 top-0 w-[2px] bg-primary transition-all duration-300 md:left-1/2 md:-translate-x-1/2" style={{
          height: `${scrollProgress}%`
        }} />

          <div className="relative">
            {milestones.map((item, index) => <TimelineItem key={`${item.year}-${item.title}`} item={item} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}